import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";

export default function Auth() {
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(sp.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.onboarded ? "/dashboard" : "/onboarding");
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        await signup(email, password, name);
        toast.success("Welcome! Let's set up your profile.");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${apiBase}/api/auth/google/authorize`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col justify-center p-8 sm:p-16 bg-white">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <img src="https://customer-assets.emergentagent.com/job_jd-mailer-pro/artifacts/25t7dqsa_Untitled%20design%20%2835%29.png" alt="Aplica" className="h-8 w-8 rounded-md" />
          <span className="font-display font-semibold text-lg">Aplica</span>
        </Link>
        <div className="max-w-sm w-full">
          <span className="label-xs">{mode === "signup" ? "Create account" : "Welcome back"}</span>
          <h1 className="mt-2 text-4xl font-display tracking-tighter">
            {mode === "signup" ? "Start sending better outreach." : "Log back in."}
          </h1>
          <p className="mt-3 text-slate-600">
            {mode === "signup" ? "Free — no card required. 10 emails on us." : "Pick up where you left off."}
          </p>

          <button
            onClick={handleGoogle}
            className="mt-8 w-full border border-slate-200 hover:bg-slate-50 py-3 rounded-md font-medium flex items-center justify-center gap-2 transition-colors"
            data-testid="google-login-button"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> OR <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="label-xs block mb-1.5">Name</label>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name-input"
                  className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
              </div>
            )}
            <div>
              <label className="label-xs block mb-1.5">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
            </div>
            <div>
              <label className="label-xs block mb-1.5">Password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="auth-submit-button"
              className="w-full btn-primary justify-center">
              {loading ? "Please wait…" : mode === "signup" ? "Create account" : "Log in"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-500">
            {mode === "signup" ? "Already have an account? " : "New here? "}
            <button onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              className="text-[#0047FF] hover:underline font-medium"
              data-testid="auth-toggle-mode">
              {mode === "signup" ? "Log in" : "Create an account"}
            </button>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/31726663/pexels-photo-31726663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt="Freelancer workspace"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-display text-2xl leading-tight max-w-md">
            "I went from rewriting the same cold email 5 times a day to sending 40 personalized applications in a single sitting."
          </p>
          <p className="mt-4 text-sm text-white/80">— Priya, Full-stack freelancer</p>
        </div>
      </div>
    </div>
  );
}
